var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useMemo } from "react";
import DOMPurify from "dompurify";
import QuestionnaireOrg from "../../../organisms/jsQuestionnaire/jsQuestionnaire";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AntecedentsAction, AntecedentsFormAction, KnowledgeAction, GeneralSkillsAction, ConfidenceAction, AttributesAction, QualificationsAction, SocialAction, PersonalWellbeingAction, ProgressionAction, GlobalAction, ClientObligationsAction, AdviserObligationsAction, AdviserFeedbackAction, AdviserImpactAction, OverallExperienceAction, } from "../../../../redux/actions/job_seeker_questionnaire";
import { questionnaireTimer } from "../../../../utils/QuestionnaireTimer";
import { getQuestionnaireAttemptVariant, getQuestionnaireProgressWidth, } from "../../../../utils/questionnaireProgress";
/**
 * The Antecedents Questionnaire Page
 * This will need to call all relevant APIs to get the values.
 * @param props
 * @returns
 */
function AntecedentsQuestionnaire() {
    var navigate = useNavigate();
    var dispatch = useDispatch();
    var _a = useState(""), error = _a[0], setError = _a[1];
    var _b = useState(false), isSubmitting = _b[0], setIsSubmitting = _b[1];
    var _c = useState({}), liveFormData = _c[0], setLiveFormData = _c[1];
    var _d = useState({}), disabledCheckboxOptions = _d[0], setDisabledCheckboxOptions = _d[1];
    var NO_OTHER_LANGUAGES = "No other languages";
    var PREFER_NOT_TO_SAY = "Prefer not to say";
    var EXCLUSIVE_CHECKBOX_FIELDS = {
        jobseeker_other_languages: [NO_OTHER_LANGUAGES, PREFER_NOT_TO_SAY],
        jobseeker_dependents: ["None", PREFER_NOT_TO_SAY],
        jobseeker_disabilitycondition: ["None", PREFER_NOT_TO_SAY],
        jobseeker_kycchecklist: ["None of the above"],
    };
    var _e = useSelector(function (state) { return state.rootReducer.JobSeekerQuestionnaireReducer; }), antecedentsData = _e.antecedentsData, antecedentsFormData = _e.antecedentsFormData, success_antecedents_form = _e.success_antecedents_form;
    var loginData = useSelector(function (state) { return state.rootReducer.userReducer; }).loginData;
    var quesAttemptDetails = useSelector(function (state) { return state.rootReducer.dashboardReducer.quesAttemptDetails; });
    var isNonFirstAttempt = !!((quesAttemptDetails === null || quesAttemptDetails === void 0 ? void 0 : quesAttemptDetails.second_attempt_ready) ||
        (quesAttemptDetails === null || quesAttemptDetails === void 0 ? void 0 : quesAttemptDetails.milestone_questionnaire_ready));
    var getFieldChoices = function (fieldKey) {
        var _a, _b;
        var fieldItem = antecedentsData === null || antecedentsData === void 0 ? void 0 : antecedentsData.find(function (item) { return Object.keys(item)[0] === fieldKey; });
        return fieldItem ? (_b = (_a = fieldItem[fieldKey]) === null || _a === void 0 ? void 0 : _a.choices) !== null && _b !== void 0 ? _b : [] : [];
    };
    var normalizeExclusiveCheckboxSelection = function (fieldKey, selectedValues) {
        if (selectedValues === void 0) { selectedValues = []; }
        var exclusiveValues = EXCLUSIVE_CHECKBOX_FIELDS[fieldKey];
        if (!exclusiveValues) {
            return { values: selectedValues, disabled: new Set() };
        }
        var uniqueSelected = selectedValues.filter(function (val, index, arr) { return arr.indexOf(val) === index; });
        var selectedExclusive = uniqueSelected.filter(function (val) {
            return exclusiveValues.includes(val);
        });
        var selectedOther = uniqueSelected.filter(function (val) { return !exclusiveValues.includes(val); });
        if (selectedExclusive.length > 0) {
            var activeExclusive = selectedExclusive[selectedExclusive.length - 1];
            return {
                values: [activeExclusive],
                disabled: new Set(getFieldChoices(fieldKey).filter(function (choice) { return !exclusiveValues.includes(choice); })),
            };
        }
        return { values: selectedOther, disabled: new Set() };
    };
    var formattedResponses = useMemo(function () {
        if (!antecedentsFormData)
            return antecedentsFormData;
        var formatted = __assign({}, antecedentsFormData);
        var arrayFields = [
            "jobseeker_dependents",
            "jobseeker_disabilitycondition",
            "jobseeker_kycchecklist",
            "jobseeker_other_languages",
        ];
        arrayFields.forEach(function (field) {
            if (Array.isArray(formatted[field])) {
                formatted[field] = formatted[field].join(",");
            }
        });
        return formatted;
    }, [antecedentsFormData]);
    useEffect(function () {
        questionnaireTimer.startSection("Demographics");
        dispatch(AntecedentsAction());
        // Prefetch the rest of the jobseeker questionnaire sections in the background
        dispatch(KnowledgeAction());
        dispatch(GeneralSkillsAction());
        dispatch(ConfidenceAction());
        dispatch(AttributesAction());
        dispatch(QualificationsAction());
        dispatch(SocialAction());
        dispatch(PersonalWellbeingAction());
        dispatch(ProgressionAction());
        dispatch(GlobalAction());
        // Both the first and second attempt end with the obligations sections
        dispatch(ClientObligationsAction());
        dispatch(AdviserObligationsAction());
    }, [dispatch]);
    useEffect(function () {
        // Only prefetch the second-attempt-only sections if the jobseeker is on their second attempt
        if (isNonFirstAttempt) {
            dispatch(AdviserFeedbackAction());
            dispatch(AdviserImpactAction());
            dispatch(OverallExperienceAction());
        }
    }, [dispatch, isNonFirstAttempt]);
    useEffect(function () {
        if (formattedResponses) {
            setLiveFormData(formattedResponses);
            var nextDisabled_1 = {};
            Object.keys(EXCLUSIVE_CHECKBOX_FIELDS).forEach(function (fieldKey) {
                var rawValue = formattedResponses[fieldKey];
                var existing = Array.isArray(rawValue)
                    ? rawValue
                    : typeof rawValue === "string"
                        ? rawValue
                            .split(",")
                            .map(function (item) { return item.trim(); })
                            .filter(Boolean)
                        : [];
                var disabled = normalizeExclusiveCheckboxSelection(fieldKey, existing).disabled;
                if (disabled.size > 0) {
                    nextDisabled_1[fieldKey] = disabled;
                }
            });
            setDisabledCheckboxOptions(nextDisabled_1);
        }
    }, [formattedResponses, antecedentsData]);
    var mainLanguageIsOther = liveFormData["jobseeker_main_language"] === "Other";
    var otherLanguagesIncludesOther = (liveFormData["jobseeker_other_languages"] || []).includes("Any other languages");
    var filteredData = useMemo(function () {
        var _a;
        return ((_a = antecedentsData === null || antecedentsData === void 0 ? void 0 : antecedentsData.filter(function (item) {
            var key = Object.keys(item)[0];
            if (key === "jobseeker_english_ability" && !mainLanguageIsOther)
                return false;
            if (key === "jobseeker_main_language_other" && !mainLanguageIsOther)
                return false;
            if (key === "jobseeker_other_languages_other" &&
                !otherLanguagesIncludesOther)
                return false;
            return true;
        })) !== null && _a !== void 0 ? _a : []);
    }, [antecedentsData, mainLanguageIsOther, otherLanguagesIncludesOther]);
    var customValidation = function (formData) {
        var _a, _b, _c;
        var errors = {};
        if (formData.jobseeker_main_language === "Other" &&
            !((_a = formData.jobseeker_main_language_other) === null || _a === void 0 ? void 0 : _a.trim())) {
            errors.jobseeker_main_language_other = "Please specify your main language.";
        }
        if (((_b = formData.jobseeker_other_languages) === null || _b === void 0 ? void 0 : _b.includes("Any other languages")) &&
            !((_c = formData.jobseeker_other_languages_other) === null || _c === void 0 ? void 0 : _c.trim())) {
            errors.jobseeker_other_languages_other = "Please specify the other language.";
        }
        return errors;
    };
    var toTitleCase = function (str) {
        if (!str)
            return str;
        return str
            .toLowerCase()
            .split(' ')
            .map(function (word) { return word.charAt(0).toUpperCase() + word.slice(1); })
            .join(' ');
    };
    var handleFormChange = function (newFormData) {
        var updatedFormData = __assign({}, newFormData);
        var nextDisabled = __assign({}, disabledCheckboxOptions);
        Object.keys(EXCLUSIVE_CHECKBOX_FIELDS).forEach(function (fieldKey) {
            var normalized = normalizeExclusiveCheckboxSelection(fieldKey, newFormData[fieldKey] || []);
            updatedFormData[fieldKey] = normalized.values;
            if (normalized.disabled.size > 0) {
                nextDisabled[fieldKey] = normalized.disabled;
            }
            else {
                delete nextDisabled[fieldKey];
            }
        });
        setDisabledCheckboxOptions(nextDisabled);
        setLiveFormData(updatedFormData);
        return updatedFormData;
    };
    var handleFormSubmit = function (formData) {
        questionnaireTimer.endSection("Demographics");
        var cleaned = __assign({}, formData);
        if (cleaned["jobseeker_main_language"] !== "Other") {
            delete cleaned["jobseeker_main_language_other"];
            delete cleaned["jobseeker_english_ability"];
        }
        if (!(cleaned["jobseeker_other_languages"] || "").includes("Any other languages")) {
            delete cleaned["jobseeker_other_languages_other"];
        }
        var sanitizedData = Object.keys(cleaned).reduce(function (acc, key) {
            var value = DOMPurify.sanitize(cleaned[key]);
            if (key === "jobseeker_main_language_other" || key === "jobseeker_other_languages_other") {
                value = toTitleCase(value);
            }
            acc[key] = value;
            return acc;
        }, {});
        // Define the fields for ArrayFields
        var arrayFields = [
            "jobseeker_dependents",
            "jobseeker_disabilitycondition",
            "jobseeker_kycchecklist",
            "jobseeker_other_languages",
        ];
        // Convert comma-separated strings to arrays
        arrayFields.forEach(function (field) {
            if (typeof sanitizedData[field] === "string") {
                sanitizedData[field] = sanitizedData[field]
                    .split(",")
                    .map(function (item) { return item.trim(); })
                    .filter(Boolean); // Drops empty strings
            }
            else if (!sanitizedData[field]) {
                sanitizedData[field] = [];
            }
        });
        setIsSubmitting(true);
        dispatch(AntecedentsFormAction(sanitizedData, loginData));
    };
    var handleBackBtn = function () { return navigate("/"); };
    useEffect(function () {
        if (success_antecedents_form && isSubmitting) {
            navigate("/questionnaire/knowledge");
        }
    }, [success_antecedents_form, isSubmitting, navigate]);
    return (_jsxs("div", __assign({ className: "" }, { children: [_jsx(QuestionnaireOrg, { title: "Demographics", content: "For each of the following sections please tick the box that best represents your situation.", data: filteredData, responses: formattedResponses, loading: !antecedentsData || antecedentsData.length === 0, handleSubmit: handleFormSubmit, handleBackBtn: handleBackBtn, onChange: handleFormChange, classname: "antecedents-w", width: getQuestionnaireProgressWidth("antecedents", getQuestionnaireAttemptVariant(quesAttemptDetails)), customValidation: customValidation, disabledCheckboxOptions: disabledCheckboxOptions }), error && _jsx("div", __assign({ className: "error-message" }, { children: error }))] })));
}
export default AntecedentsQuestionnaire;
